/*
 * IDRedirect.ts (AbstractLicensingBackend)
 *
 * Copyright © 2026 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * @file IDRedirect.ts
 * @copyright 2026 InstaLOD GmbH. All rights reserved.
 * @section License
 */

import { REACT_APP_USER_FRONTEND_BASE_URL } from '../config';
import { isStringEmptyOrNullOrUndefined } from '@abstract/abstractwebcommon-shared/utils/sharedFunctions';

/**
 * Build the URL of this product's licence page on AbstractID, which is where
 * users without the admin role manage their licences and activations.
 *
 * The session token is handed over in the fragment rather than the query, so
 * it never reaches a server, a proxy or an access log; AbstractID reads it on
 * load and strips it from the address bar.
 *
 * @param userFrontendBaseURL AbstractID frontend base URL from the safe settings
 * @param applicationUUID This product's application UUID in AbstractID
 * @param token The user's AbstractID session token
 * @returns The URL, or null when no AbstractID frontend is configured
 */
export const getIDLicenseURL = (
  userFrontendBaseURL: string | null | undefined,
  applicationUUID: string | null | undefined,
  token: string | null | undefined
): string | null => {
  const baseURL: string | undefined = !isStringEmptyOrNullOrUndefined(userFrontendBaseURL)
    ? userFrontendBaseURL
    : REACT_APP_USER_FRONTEND_BASE_URL;
  if (isStringEmptyOrNullOrUndefined(baseURL)) {
    return null;
  }

  const path: string = isStringEmptyOrNullOrUndefined(applicationUUID)
    ? '/Manage/Licenses'
    : `/Manage/Licenses/${encodeURIComponent(applicationUUID)}`;
  const fragment: string = isStringEmptyOrNullOrUndefined(token)
    ? ''
    : `#token=${encodeURIComponent(token)}`;

  return `${baseURL.replace(/\/+$/, '')}${path}${fragment}`;
};
